/*
#############################################################################
# Copyright (c) 2022-2023 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
#
# NVIDIA CORPORATION, its affiliates and licensors retain all intellectual
# property and proprietary rights in and to this material, related
# documentation and any modifications thereto. Any use, reproduction,
# disclosure or distribution of this material and related documentation
# without an express license agreement from NVIDIA CORPORATION or
# its affiliates is strictly prohibited.
###############################################################################
*/

/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-console */
import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
// import DialogTitle from "@mui/material/DialogTitle";
import ReactStars from "react-rating-stars-component";
import { useSelector } from "react-redux";
import axios from "axios";
import Typography from "@mui/material/Typography";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAltOutlined";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";

import Avatar from "@mui/material/Avatar";
import ListItemText from "@mui/material/ListItemText";
import ListItem from "@mui/material/ListItem";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Toppings from "../topping-customization/Toppings";
import config from "../../config/config";

const StyledBox = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.grey[900],
  bottom: 0,
  left: 0,
  position: "absolute",
  width: "100%",
}));

const customIcons = {
  1: {
    icon: <SentimentVeryDissatisfiedIcon color='error' sx={{ fontSize: 80 }} />,
    label: "Very Dissatisfied",
  },
  2: {
    icon: <SentimentDissatisfiedIcon color='error' sx={{ fontSize: 80 }} />,
    label: "Dissatisfied",
  },
  3: {
    icon: <SentimentSatisfiedIcon color='warning' sx={{ fontSize: 80 }} />,
    label: "Neutral",
  },
  4: {
    icon: <SentimentSatisfiedAltIcon color='success' sx={{ fontSize: 80 }} />,
    label: "Satisfied",
  },
  5: {
    icon: <SentimentVerySatisfiedIcon color='success' sx={{ fontSize: 80 }} />,
    label: "Very Satisfied",
  },
};

function IconContainer(props) {
  const { value, ...other } = props;
  return <span {...other}>{value !== -1 ? customIcons[value].icon : ""}</span>;
}

const FeedbackDialog = forwardRef((props, ref) => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(-1);
  const [showCart, setShowCart] = useState(true);
  const sessionId = useSelector((state) => state.uuid.sessionId);
  const streamId = useSelector((state) => state.uuid.streamId);
  const sessionActive = useSelector((state) => state.uuid.sessionActive);

  const itemsInCart = window.cart ? window.cart : [];
  const totalOrderCost = window.totalOrderCost ? window.totalOrderCost : 0;

  let timeout;

  useEffect(() => {
    setTimeout(() => {
      setShowCart(false);
    }, 3000);
  }, []);

  useEffect(() => {
    if(sessionActive) {
      setOpen(false);
      if(timeout) {
        clearTimeout(timeout);
      }
    }
  }, [sessionActive]);

  useEffect(() => {
    timeout = setTimeout(() => {
      setOpen(false);
    }, 10000);
  }, []);

  const closeFeedbackDialog = (event, reason) => {
    if (reason !== "escapeKeyDown" && reason !== "backdropClick") {
      setOpen(false);
    }
  };

  const sendUserFeedback = (val) => {
    window.cart = null;
    window.totalOrderCost = null;
    const jsonData = {
      "sessionId": sessionId,
      "streamId": streamId,
      "score": val,
    };
    console.debug("posting rating", jsonData);
    const promise = axios(`${config.uiServerEndpoint}/manage/rating`, {
      method: "post",
      data: jsonData,
    });
    promise.then((response) => {
      console.log("rating sent successfully", response.data);
    });
    promise.catch((e) => {
      console.error("unable to send ratings ", e);
    });
  };

  const handleRating = (val) => {
    sendUserFeedback(val);
    setRating(val);
    setTimeout(() => {
      setOpen(false);
    }, 1000);
  };

  useImperativeHandle(ref, () => ({
    openFeedbackDialog() {
      setOpen(true);
    },
  }));

  return (
    <div>
      <Dialog open={open} onClose={closeFeedbackDialog} fullWidth>
        {!showCart && (
          <>
            <Typography variant='h5' align='center' sx={{ marginTop: 1 }}>
              Submit feedback to continue
            </Typography>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                alignContent: "center",
                flexGrow: 1,
                flexDirection: "column",
              }}
            >
              <IconContainer value={rating} />
              {rating === -1 && (
                <ReactStars
                  size={60}
                  half={false}
                  onChange={(newRating) => {
                    handleRating(newRating);
                  }}
                />
              )}
            </div>
          </>
        )}
        {showCart && (
          <Typography variant='h5' align='center' sx={{ marginTop: 1 }}>
            Order successful
          </Typography>
        )}
        {showCart && (
          <DialogContent>
            <div>
              <Box>
                <List sx={{ marginBottom: 1 }}>
                  {itemsInCart.map((item, index) => (
                    <ListItem
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${index}_cart`}
                      secondaryAction={
                        <Typography variant='h5' style={{ marginRight: 15 }}>
                          ${item.price.toFixed(2)}
                        </Typography>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar
                          alt='Profile Picture'
                          src={item.image_loc}
                          sx={{
                            borderRadius: 0,
                            objectFit: "contain",
                            width: 70,
                            height: 70,
                            marginRight: 1,
                          }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        disableTypography
                        primary={
                          <>
                            <Typography variant='h5'>{`${item.name}, ${item.size}`}</Typography>
                            <Typography
                              variant='h5'
                              color='text.secondary'
                              style={{
                                fontSize: "0.9rem",
                                marginRight: 2,
                                maxWidth: "90%",
                              }}
                            >
                              {`Estimated ${item.calories.toFixed(0)} Calories`}
                              <br />
                              <Toppings toppings={item.toppings} />
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                <StyledBox>
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant='h6'
                      paddingX={2}
                      paddingY={1}
                      sx={{
                        marginLeft: 4,
                      }}
                    >
                      Total
                    </Typography>
                    <Typography
                      variant='h5'
                      paddingX={2}
                      paddingY={1}
                      sx={{
                        marginRight: 4,
                      }}
                    >
                      ${totalOrderCost}
                    </Typography>
                  </Box>
                </StyledBox>
              </Box>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
});

export default FeedbackDialog;
